import { useForm } from 'react-hook-form';
import {
  Info as InfoIcon,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Tooltip,
  Typography,
  DownloadIcon,
  SnackbarStatus,
  StatusBar,
  Checkbox,
  Spinner,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import chefPlating from 'src/assets/images/chef-plating.png';
import styles from './PriceSchemeImportReviewModal.module.scss';
import { usePriceSchemeOverview } from 'src/hooks/usePriceSchemeOverview';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { useEffect } from 'react';
import { useDownloadBlob } from 'src/hooks/useDownloadBlob/useDownloadBlob';
import { PriceSchemeOverviewResponse } from 'src/api/getPriceSchemeOverview/getPriceSchemeOverview';

type PriceSchemeImportReviewModalProps = {
  cancelText: string;
  onAccept: () => void;
  onReject: () => void;
  eventId: string;
};

interface SelectPriceSchemeSchema {
  'ack-errors'?: boolean;
  'ack-missing'?: boolean;
}

const PriceSchemeImportReviewModal = ({ onReject, onClose, onAccept, open, eventId, cancelText = 'Reject Import' }) => {
  return (
    <Modal open={open} closeModal={onClose} image={chefPlating} imageAlt="Chef plating">
      <PriceSchemeImportReview onReject={onReject} onAccept={onAccept} cancelText={cancelText} eventId={eventId} />
    </Modal>
  );
};

const getValidCount = (overview?: PriceSchemeOverviewResponse['data']) => {
  if (!overview?.info) {
    return 0;
  }

  return (
    ('items_with_prices_count' in overview.info
      ? overview.info.items_with_prices_count
      : overview.info.item_price_changes_count) ?? 0
  );
};

const PriceSchemeImportReview = ({ onAccept, onReject, cancelText, eventId }: PriceSchemeImportReviewModalProps) => {
  const { errorMessage, showNotification } = useModalNotification();
  const { data: overview, isLoading: overviewLoading, isError, error } = usePriceSchemeOverview(eventId);
  const downloadErrorReport = useDownloadBlob(overview?.errors?.report_url, overview?.file_name);

  // Valid count uses a different key depending on partial/full price scheme import
  const validCount = getValidCount(overview);
  const fileName = overview?.file_name ?? '';
  const errorCount = overview?.errors?.count ?? 0;
  const errorsRecorded = errorCount > 0;

  const { handleSubmit, formState, register } = useForm<SelectPriceSchemeSchema>({
    defaultValues: { 'ack-errors': false, 'ack-missing': false },
  });

  const onDownloadErrorReport = async () => {
    try {
      await downloadErrorReport.download();
    } catch (error) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Unable to download error report',
      });
    }
  };

  const { isLoading, isSubmitting, isValid } = formState;

  useEffect(() => {
    if (isError) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'Unable to import price scheme',
      });
    }
  }, [isError, error]);

  if (overviewLoading || isError) {
    return (
      <>
        <SpinnerContainer data-testid="loadingIcon">
          <Spinner size="xlarge" colour="rainbow" />
        </SpinnerContainer>
        <div className={styles['error-banner']}>
          {errorMessage && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit(onAccept)}>
      <Typography size="xxlarge" weight="semi-bold">
        Scheme Import Overview
      </Typography>
      <Typography>
        Import breakdown from your CSV file <b>{fileName}</b>
      </Typography>
      <section className={styles['overview-section']}>
        <div>
          <Typography weight="semi-bold">Valid Products</Typography>
          <Typography>{validCount} products</Typography>
        </div>
        {errorsRecorded && (
          <>
            <div>
              <Tooltip
                position="top"
                hasArrow={false}
                text="Download and review the error report. You can ignore the errors and proceed with the import if acceptable."
              >
                <div className={styles['tooltip-container']}>
                  <Typography weight="semi-bold">Import Errors</Typography>
                  <InfoIcon />
                </div>
              </Tooltip>
              <Typography>{errorCount}</Typography>
            </div>
            <div className={styles['error-container']}>
              <Typography weight="semi-bold">Download your file with the Error Report</Typography>
              <SecondaryButton
                aria-label="Download error report"
                buttonStyle="ghost"
                disabled={downloadErrorReport.isFetching}
                type="button"
                onClick={onDownloadErrorReport}
                className={styles['download-errors-button']}
                icon={() => <DownloadIcon size="small" />}
              >
                <Typography weight="bold">Download</Typography>
              </SecondaryButton>
              <Checkbox
                required
                labelWeight="regular"
                label="I acknowledge that by continuing any unrecognised products will not be imported and need to be added via the product library or product sync."
                name="ack-errors"
                register={register}
              />
              <Checkbox
                required
                labelWeight="regular"
                label="I acknowledge that by continuing any products with missing or invalid prices will be given null value which will need to be rectified before attempting to publish."
                name="ack-missing"
                register={register}
              />
            </div>
          </>
        )}
      </section>
      <ButtonContainer
        left={
          <SecondaryButton data-cy="cancel-button" buttonStyle="outline" onClick={onReject} size="full-width">
            {cancelText}
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            data-cy="create-button"
            loading={isSubmitting}
            disabled={isLoading || isSubmitting || !isValid}
            size="full-width"
            type="submit"
          >
            Accept Import
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export default PriceSchemeImportReviewModal;
